import { useEffect, useState } from "react";
import { toast } from "sonner";
import { setJobTowAuthority } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * Inline 8-digit numeric editor for a job/impound's Tow Authority Number.
 *
 * Behaviour:
 *  - Up to 8 digits, numbers only. Non-digit keystrokes are silently
 *    stripped so we never send invalid input to the server.
 *  - Saves on Enter or when the input loses focus — dispatchers and
 *    back-office users tab through rows, so per-keystroke autosave would be noisy.
 *  - Toast on success/failure, reverts to last-known-good value on error.
 *  - Auto-syncs if the row is refreshed elsewhere (polling, edits from
 *    another tab/page).
 *
 * Used by HistoryPage, ImpoundsPage and ImpoundHistoryPage. All three
 * pages share the same edit semantics so a single component keeps them
 * consistent (and dark-mode safe).
 */
export default function TowAuthorityCell({ job, disabled, onSaved, testIdPrefix = "tow-authority" }) {
  const [value, setValue] = useState(job.tow_authority_number || "");
  const [saving, setSaving] = useState(false);
  const original = job.tow_authority_number || "";

  useEffect(() => {
    setValue(job.tow_authority_number || "");
  }, [job.tow_authority_number]);

  const persist = async () => {
    const trimmed = value.trim();
    if (trimmed === original) return;
    if (trimmed && (!/^\d+$/.test(trimmed) || trimmed.length > 8)) {
      toast.error("Tow Authority Number must be up to 8 digits, numbers only");
      setValue(original);
      return;
    }
    setSaving(true);
    try {
      const updated = await setJobTowAuthority(job.id, trimmed);
      onSaved?.(updated);
      if (trimmed) toast.success(`Tow Authority # saved (${trimmed})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save Tow Authority #");
      setValue(original);
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="\d*"
      maxLength={8}
      value={value}
      disabled={disabled || saving}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 8))}
      onBlur={persist}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      placeholder="—"
      className={cn(
        "w-16 px-2 py-1 text-xs font-mono tabular-nums rounded border bg-white dark:bg-slate-900 dark:text-slate-100",
        "border-slate-300 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500",
        saving && "opacity-50",
        disabled && "opacity-40 cursor-not-allowed",
      )}
      title={disabled
        ? "Locked — record is archived"
        : "Up to 8 digits — saved on Enter or when you click away"}
      data-testid={`${testIdPrefix}-${job.id}`}
    />
  );
}
